<script setup lang="ts">
import ControlValidation from "@/components/control-validation.vue";
import { useControlValidation } from "@/composables/use-control-validation";
import type { ContractDTO } from "@/models/dtos/contract-dto";
import type { UserDTO } from "@/models/dtos/user-dto";
import type { UpdateUserRequest } from "@/models/requests/update-user-request";
import { getUpdateUserSchema } from "@/schemas/update-user-schema";
import { contractsService } from "@/services/contracts-service";
import { notificationsService } from "@/services/notifications-service";
import { usersService } from "@/services/users-service";
import { mdiArrowLeftBold, mdiArrowRightBold } from "@mdi/js";
import { onBeforeMount, ref, toRefs } from "vue";

function getDefaultFormData(user?: Partial<UserDTO>): UpdateUserRequest {
  return {
    name: user?.name ?? "",
    phone: user?.phone ?? "",
    email: user?.email ?? "",
  } as UpdateUserRequest;
}

const props = defineProps<{
  userId: string | number;
  customerNumber: string;
}>();
const { userId, customerNumber } = toRefs(props);

const pageLoading = ref(false);
const loading = ref(false);
const user = ref<undefined | UserDTO>();
const userContracts = ref<ContractDTO[]>([]);
const availableContracts = ref<ContractDTO[]>([]);

const formData = ref(getDefaultFormData());

const { result, validate } = useControlValidation(() => getUpdateUserSchema());

const selectedContractId = ref(null);

onBeforeMount(async () => {
  try {
    pageLoading.value = true;
    user.value = await usersService.getUser(userId.value);
    formData.value = getDefaultFormData(user.value);

    await loadContracts();
  } catch (error) {
    notificationsService.error(error);
  } finally {
    pageLoading.value = false;
  }
});

async function loadContracts() {
  try {
    if (!user.value) return;

    const { id: _userId } = user.value;

    if (!customerNumber.value) console.error("customerNumber is not defined");

    availableContracts.value = await contractsService.getContracts({
      customerNumber: customerNumber.value,
      excludeUserId: _userId,
    });
    userContracts.value = await contractsService.getContracts({
      customerNumber: customerNumber.value,
      includeUserId: _userId,
    });
  } catch (error) {
    notificationsService.error(error);
  }
}

async function onUpdate() {
  loading.value = true;

  try {
    const userId = user.value?.id;
    const valid = validate(formData.value);
    if (!valid || !userId) return;

    user.value = await usersService.updateUser(userId, formData.value);
    notificationsService.open({
      text: "Användare uppdaterad.",
      type: "success",
    });
  } catch (error) {
    notificationsService.error(error);
  } finally {
    loading.value = false;
  }
}

async function onAddContract() {
  const userId = user.value?.id;
  const contractId = selectedContractId.value;
  if (!userId || !contractId) return;

  try {
    await usersService.addContractToUser(userId, contractId);
    await loadContracts();
  } catch (error) {
    notificationsService.error(error);
  }
}

async function onRemoveContract() {
  const userId = user.value?.id;
  const contractId = selectedContractId.value;
  if (!userId || !contractId) return;

  try {
    await usersService.removeContractFromUser(userId, contractId);
    await loadContracts();
  } catch (error) {
    notificationsService.error(error);
  }
}
</script>

<template>
  <section class="section px-0">
    <p class="title is-4">{{ user?.name }}</p>

    <app-loader v-if="pageLoading"></app-loader>

    <div v-else-if="user">
      <div class="columns">
        <div class="column is-3">
          <section class="section notification is-light">
            <form @submit.prevent="onUpdate">
              <!--              <div class="field">-->
              <!--                <label class="label" for="username">Användarnamn</label>-->
              <!--                <div class="control">-->
              <!--                  <input id="username" class="input" readonly disabled :value="user.username" />-->
              <!--                </div>-->
              <!--              </div>-->

              <div class="field">
                <label class="label" for="name">Namn</label>
                <control-validation
                  field="name"
                  :result="result"
                  #="{ inputClass }"
                >
                  <div class="control">
                    <input
                      id="name"
                      v-model="formData.name"
                      class="input"
                      type="text"
                      :class="inputClass"
                    />
                  </div>
                </control-validation>
              </div>

              <div class="field">
                <label class="label" for="phone">Telefonummer</label>
                <control-validation
                  field="phone"
                  :result="result"
                  #="{ inputClass }"
                >
                  <div class="control">
                    <input
                      id="phone"
                      v-model="formData.phone"
                      class="input"
                      type="tel"
                      :class="inputClass"
                    />
                  </div>
                </control-validation>
              </div>

              <div class="field">
                <label class="label" for="email">E-post</label>
                <control-validation
                  field="email"
                  :result="result"
                  #="{ inputClass }"
                >
                  <div class="control">
                    <input
                      id="email"
                      v-model="formData.email"
                      class="input"
                      type="email"
                      readonly
                      disabled
                      :class="inputClass"
                    />
                  </div>
                </control-validation>
              </div>

              <div class="control">
                <button
                  class="button is-primary"
                  type="submit"
                  :class="{ 'is-loading': loading }"
                >
                  Uppdatera
                </button>
              </div>
            </form>
          </section>
        </div>

        <div class="column">
          <section class="section notification has-background-violet-light">
            <div class="field">
              <label for="availableContracts" class="label"
                >Tillgängliga affärer</label
              >
              <div class="control">
                <div class="select is-multiple is-fullwidth">
                  <select
                    id="availableContracts"
                    v-model="selectedContractId"
                    name="availableContracts"
                    size="10"
                  >
                    <option
                      v-for="contract in availableContracts"
                      :key="contract.id"
                      :value="contract.id"
                    >
                      {{ contract.name }} ({{ contract.eppsusId }})
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </section>
        </div>

        <div
          class="column is-narrow is-flex is-flex-direction-column is-justify-content-center"
        >
          <div class="control mb-4">
            <button
              class="button is-medium"
              aria-label="Lägg till vald affär"
              @click="onAddContract"
            >
              <span class="icon">
                <app-icon :icon="mdiArrowRightBold" size="36" />
              </span>
            </button>
          </div>

          <div class="control">
            <button
              class="button is-medium"
              aria-label="Ta bort vald affär"
              @click="onRemoveContract"
            >
              <span class="icon">
                <app-icon :icon="mdiArrowLeftBold" size="36" />
              </span>
            </button>
          </div>
        </div>

        <div class="column">
          <section class="section notification has-background-violet-light">
            <div class="field">
              <label for="userContracts" class="label">Valda affärer</label>
              <div class="control">
                <div class="select is-multiple is-fullwidth">
                  <select
                    id="userContracts"
                    v-model="selectedContractId"
                    name="userContracts"
                    size="10"
                  >
                    <option
                      v-for="contract in userContracts"
                      :key="contract.id"
                      :value="contract.id"
                    >
                      {{ contract.name }} ({{ contract.eppsusId }})
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.select select {
  min-height: 300px;
  max-height: 300px;
}
</style>
